import { motion } from "framer-motion";
import { Award, Target, ShieldCheck, ShieldAlert, Lightbulb, Zap } from "lucide-react";

const gradeStyle = (score) => {
  if (score === "A+" || score === "A")
    return { color: "#00FF41", glow: "glow-green", border: "border-[#00FF41]", bg: "bg-[#00FF41]/5" };
  if (score === "B")
    return { color: "#FFD700", glow: "glow-yellow", border: "border-[#FFD700]", bg: "bg-[#FFD700]/5" };
  return { color: "#FF003C", glow: "glow-red", border: "border-[#FF003C]", bg: "bg-[#FF003C]/5" };
};

const Section = ({ icon: Icon, title, children, accent, testid }) => (
  <div className="border border-[#1A1A24] bg-[#0C0C0E] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4" style={{ color: accent }} />
      <h4 className="text-[11px] tracking-[0.2em] uppercase text-[#848D97]">{title}</h4>
    </div>
    <div className="text-sm text-[#E2E8F0] leading-relaxed">{children}</div>
  </div>
);

export default function GradingCard({ grading }) {
  if (!grading) return null;
  const s = gradeStyle(grading.icc_score);

  return (
    <motion.div
      initial={{ opacity: 0, filter: "blur(8px)", y: 20 }}
      animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="grading-card"
      className={`border ${s.border} ${s.glow} ${s.bg} bg-[#0C0C0E]`}
    >
      <div className={`flex items-center justify-between p-6 border-b ${s.border}`}>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#848D97] mb-1">ICC Validation Score</div>
          <div className="flex items-center gap-2 text-[#E2E8F0] text-sm">
            <Zap className="w-4 h-4" style={{ color: s.color }} /> Setup graded
          </div>
        </div>
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 220 }}
          className="font-display font-black leading-none"
          style={{ fontSize: "4rem", color: s.color, textShadow: `0 0 24px ${s.color}` }}
          data-testid="grade-value"
        >
          {grading.icc_score}
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1A1A24]">
        <Section icon={Award} title="ICC Sequence" accent={s.color} testid="grading-reasoning">
          {grading.icc_reasoning}
        </Section>
        <Section icon={Target} title="Custom Strategy Audit" accent="#00F0FF" testid="grading-audit">
          {grading.custom_audit}
        </Section>
        <Section
          icon={grading.risk_pass ? ShieldCheck : ShieldAlert}
          title="Risk Check"
          accent={grading.risk_pass ? "#00FF41" : "#FF003C"}
          testid="grading-risk"
        >
          <span
            className="inline-block mb-1 px-2 py-0.5 text-[10px] uppercase tracking-wider"
            style={{
              color: grading.risk_pass ? "#00FF41" : "#FF003C",
              border: `1px solid ${grading.risk_pass ? "#00FF41" : "#FF003C"}`,
            }}
          >
            {grading.risk_pass ? "RULES MET" : "RULE VIOLATION"}
          </span>
          <div>{grading.risk_check}</div>
        </Section>
        <Section icon={Lightbulb} title="Key Lesson" accent="#FFD700" testid="grading-lesson">
          {grading.lesson}
        </Section>
      </div>
    </motion.div>
  );
}
